const emit = (target, type, detail) => target.dispatchEvent(new CustomEvent(type, { detail }));

export default class YoutubePlayer extends EventTarget {
	static #getYTPromise = null;
	#getPlayerPromise = null;

	constructor(elemId) {
		super();
		this.elemId = elemId;
	}

	static getName() { return 'Youtube'; }
	static urlMatches(url) {
		const host = new URL(url).host;
		return host === 'www.youtube.com' || host === 'm.youtube.com' || host === 'youtu.be';
	}

	static getYT() {
		return YoutubePlayer.#getYTPromise ||= new Promise((resolve, reject) => {
			window.onYouTubeIframeAPIReady = () => resolve(window.YT);
			const script = document.createElement('script');
			script.src = 'https://www.youtube.com/iframe_api';
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}

	getPlayer() {
		return this.#getPlayerPromise ||= new Promise(resolve => {
			YoutubePlayer.getYT().then(YT => {
				const player = new YT.Player(this.elemId, {
					width: '100%',
					height: '100%',
					events: {
						onReady: () => resolve(player),
						onError: ev => emit(this, 'error', ev),
						onStateChange: ev => {
							// https://developers.google.com/youtube/iframe_api_reference
							// -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
							switch(ev.data) {
								case -1: emit(this, 'status', { status: 'ready' });    break;
								case  0: emit(this, 'status', { status: 'finished' }); break;
								case  1: emit(this, 'status', { status: 'playing' });  break;
								case  2: emit(this, 'status', { status: 'paused' });   break;
								case  3: emit(this, 'status', { status: 'loading' });  break;
							}
						},
					},
				});
			});
		});
	}

	async cleanup() {
		if(!this.#getPlayerPromise) return;
		try {
			const player = await this.#getPlayerPromise;
			player.destroy();
		} catch(e) {
			// ignore
		} finally {
			this.#getPlayerPromise = null;
		}
	}

	async load(url, startSeconds, timeLimit) {
		const player = await this.getPlayer();
		if(!startSeconds || startSeconds <= 0) startSeconds = 0.00001;
		player.loadVideoById({
			videoId: this.getVideoIdFromUrl(url),
			startSeconds,
			endSeconds: timeLimit ? (startSeconds + timeLimit) : undefined,
		});
	}

	getVideoIdFromUrl(url) {
		const u = new URL(url);
		let id;
		if(u.host === 'youtu.be') {
			id = u.pathname.replace(/^\//, '');
		} else {
			const shortsMatch = u.pathname.match(/^\/shorts\/([^/?]+)/);
			id = shortsMatch ? shortsMatch[1] : u.searchParams.get('v');
		}
		if(!id) throw new TypeError('Could not extract Youtube video ID from url ' + url);
		return id;
	}

	async play() {
		const player = await this.getPlayer();
		player.playVideo();
	}

	async pause() {
		const player = await this.getPlayer();
		player.pauseVideo();
	}

	async togglePause() {
		const player = await this.getPlayer();
		if(player.getPlayerState() === 2) player.playVideo();
		else player.pauseVideo();
	}

	async stop() {
		const player = await this.getPlayer();
		player.stopVideo();
	}

	async getCurrentTime() {
		const player = await this.getPlayer();
		return player.getCurrentTime();
	}

	async setVolume(vol) {
		const player = await this.getPlayer();
		player.setVolume(vol);
	}
}
